import { Schema } from "@effect/schema";
import {
  Address,
  Amount,
  ChainKey,
  Percent,
  PERCENT_DECIMALS,
  PercentFromBigInt,
  PositionType,
  ProtocolKey,
  Usd,
} from "@silo/types";
import { parsePercentagePoints } from "@silo/utils";
import { formatUnits } from "viem";

const MerklApr = Schema.transform(Schema.Number, PercentFromBigInt, {
  decode: (value) => parsePercentagePoints(`${value}`),
  encode: (value) => {
    const str = formatUnits(value, PERCENT_DECIMALS);

    return Number(str) * 100;
  },
});

export type MerklCampaignsResponse = typeof MerklCampaignsResponse.Type;
export type MerklCampaignsResponseEncoded =
  typeof MerklCampaignsResponse.Encoded;

export const MerklCampaignsResponse = Schema.Record({
  key: Schema.String,
  value: Schema.Record({
    key: Schema.String,
    value: Schema.Record({
      key: Schema.String,
      value: Schema.Struct({
        chainId: Schema.Number,
        campaignId: Schema.String,
        campaignSubType: Schema.Literal(0, 1, 2),
        rewardToken: Schema.String,
        amount: Amount,
        startTimestamp: Schema.Number,
        endTimestamp: Schema.Number,
        campaignParameters: Schema.Struct({
          duration: Schema.BigIntFromNumber,
          whitelist: Schema.Array(Schema.String),
          targetToken: Schema.String,
          symbolRewardToken: Schema.String,
          decimalsRewardToken: Schema.Number,
        }),
        tags: Schema.optionalWith(Schema.Array(Schema.String), {
          default: () => [],
        }),
        apr: Schema.NullOr(MerklApr),
      }),
    }),
  }),
}).annotations({ identifier: "MerklCampaignsResponse" });

export type MerklCampaignMeta = typeof MerklCampaignMeta.Type;
export type MerklCampaignMetaEncoded = typeof MerklCampaignMeta.Encoded;

export const MerklCampaignMeta = Schema.Struct({
  protocolKey: ProtocolKey,
  chainKey: ChainKey,
  marketAddress: Address,
  tokenAddress: Address,
  rewardTokenAddress: Address,
  rewardTokenSymbol: Schema.String,
  rewardTokenDecimals: Schema.Number,
  pricingTokenAddress: Address,
  pricingTokenSymbol: Schema.String,
  amount: Amount,
  duration: Schema.BigInt,
  amountPerSecond: Amount,
  positionType: PositionType,
  merklApr: Schema.NullOr(Percent),
  isSuperFest: Schema.Boolean,
  isActive: Schema.Boolean,
}).annotations({ identifier: "MerklCampaignMeta" });

export type MerklCampaign = typeof MerklCampaign.Type;
export type MerklCampaignEncoded = typeof MerklCampaign.Encoded;

export const MerklCampaign = Schema.extend(
  MerklCampaignMeta,
  Schema.Struct({
    rewardTokenPriceUsd: Schema.NullOr(Usd),
  })
).annotations({ identifier: "MerklCampaign" });

export interface RawUserReward {
  chainKey: ChainKey;
  rewardTokenAddress: string;
  rewardTokenSymbol: string;
  rewardTokenDecimals: number;
  reasonKey: string;
  accumulated: Amount;
  unclaimed: Amount;
}

export type UserMerklRewardMeta = typeof UserMerklRewardMeta.Type;
export type UserMerklRewardMetaEncoded = typeof UserMerklRewardMeta.Encoded;

export const UserMerklRewardMeta = Schema.Struct({
  protocolKey: ProtocolKey,
  chainKey: ChainKey,
  marketAddress: Address,
  tokenAddress: Address,
  rewardTokenAddress: Address,
  rewardTokenSymbol: Schema.String,
  rewardTokenDecimals: Schema.Number,
  pricingTokenAddress: Address,
  pricingTokenSymbol: Schema.String,
  shareTokenAddress: Address,
  positionType: PositionType,
  unclaimed: Amount,
}).annotations({ identifier: "UserMerklRewardMeta" });

export type UserMerklReward = typeof UserMerklReward.Type;
export type UserMerklRewardEncoded = typeof UserMerklReward.Encoded;

export const UserMerklReward = Schema.extend(
  UserMerklRewardMeta,
  Schema.Struct({
    unclaimedUsd: Usd,
  })
).annotations({ identifier: "UserMerklReward" });

export type UserMerklRewardsInfo = typeof UserMerklRewardsInfo.Type;
export type UserMerklRewardsInfoEncoded = typeof UserMerklRewardsInfo.Encoded;

export const UserMerklRewardsInfo = Schema.Struct({
  protocolKey: ProtocolKey,
  user: Address,
  totalUsd: Usd,
  rewards: Schema.Array(UserMerklReward),
}).annotations({ identifier: "UserMerklRewardsInfo" });
